<script setup>
import PrimaryContainer from '@/components/PrimaryContainer.vue'
import { ref } from 'vue';
import AppLayout from '@/layouts/AppLayout.vue'
import router from '@/router';
import { computed } from 'vue';
import { useApiStore } from '@/stores/apiStore';
import WerkbonModal from '@/components/WerkbonModal.vue';
import { useWerkbonModalStore } from '@/stores/werkbonModalStore';
import TimeComponent from '@/components/TimeComponent.vue'
import ReturnButton from '@/components/ReturnButton.vue';
import { asyncComputed } from '@vueuse/core';
import { getWerkbon, getTempWerkbon } from '@/services/external-object-service';
import { useTranslationStore } from '@/stores/translationStore';
import { updateAfvalstof, validateAfvalstofData } from '@/services/afvalstof-service';
import { updateOrderDetail, validateOrderDetailData } from '@/services/opdrachtdetail-service';

const props = defineProps(['id'])

const werkbonModalStore = useWerkbonModalStore()
const apiStore = useApiStore()
const translationStore = useTranslationStore();

console.log('props.id', props.id)
apiStore.v2loadOpdracht(props.id);

const _werkbon = computed(() => apiStore.v2selectedOpdracht ?? {})
// Detail lines are locked once the order is executed, or when swtRMA is explicitly false
const isEditable = computed(() => _werkbon.value?.statusUitvoeringOpdracht !== 'Uitgevoerd' && _werkbon.value?.swtRMA !== false)
const _werkbonDetails = computed(() => apiStore.v2selectedOpdracht.details?.filter(item => item.opdrachtId == props.id) ?? [])
const _afvalstoffenData = computed(() => {
    const afvalstoffen = apiStore.v2selectedOpdracht.afvalstoffen;
    // Handle both direct array and object with afvalRegisterItems property for backward compatibility
    if (Array.isArray(afvalstoffen)) {
        return afvalstoffen;
    } else if (afvalstoffen && afvalstoffen.afvalRegisterItems) {
        return afvalstoffen.afvalRegisterItems;
    }
    return [];
})

const openWerkbonModal = async () => {
    console.log('openWerkbonModal', _werkbon.value)
    await werkbonModalStore.loadSignature(_werkbon.value?.opdrachtId)
    await werkbonModalStore.v2loadWerkbonForm(_werkbon.value?.opdrachtId)
    werkbonModalStore.modalOpen = true
}

const openBijlagen = () => router.push({ name: 'external-objects', params: { opdrachtId: props.id } })

const hasWerkbon = asyncComputed(async () => {
    const werkbon = await getWerkbon(props.id);
    return !!werkbon?.eobase64;
});

const iframe = ref(null);
const showWerkbon = ref(false);
const showWerkbonLoader = ref(false);

// Edit state management
const editingItems = ref({});
const originalValues = ref({});
const editingAfvalItems = ref({});
const originalAfvalValues = ref({});
const savingAfvalItems = ref({});
const savingItems = ref({});

// Unit options for dropdown
const unitOptions = [
    'uur',
    'm²',
    'stuks',
    'forfait',
    'FF',
    '/30min.',
    'dag',
    'week',
    'maand',
    'liter',
    'kg'
];

// Real afvalstoffen data from API - using computed property _afvalstoffenData defined above



const openWerkbon = async () => {
    showWerkbon.value = true;

    if (!hasWerkbon.value) {
        showWerkbonLoader.value = true;
        try {
            const tempWerkbon = await getTempWerkbon(props.id);
            if (!tempWerkbon || !tempWerkbon.eobase64) {
                console.error('Failed to load temporary werkbon: null or missing eobase64');
                showWerkbonLoader.value = false;
                return;
            }
            iframe.value.src = `data:application/pdf;base64, ${tempWerkbon.eobase64}#toolbar=0&navpanes=0`;
        } catch (error) {
            console.error('Error loading temporary werkbon:', error);
        } finally {
            showWerkbonLoader.value = false;
        }
    } else {
        try {
            const werkbon = await getWerkbon(props.id);
            if (!werkbon || !werkbon.eobase64) {
                console.error('Failed to load werkbon: null or missing eobase64');
                return;
            }
            iframe.value.src = `data:application/pdf;base64, ${werkbon.eobase64}#toolbar=0&navpanes=0`;
        } catch (error) {
            console.error('Error loading werkbon:', error);
        }
    }
};

// Edit functions
const startEdit = (item, index) => {
    if (!isEditable.value) return;

    // Cancel any existing edits first (single-edit mode)
    Object.keys(editingItems.value).forEach(editIndex => {
        if (editingItems.value[editIndex] && editIndex !== index.toString()) {
            // Restore original values for the currently editing item
            const editingItem = _werkbonDetails.value[editIndex];
            if (originalValues.value[editIndex] && editingItem) {
                editingItem.aantal = originalValues.value[editIndex].aantal;
            }
        }
    });
    
    // Clear all edit states
    editingItems.value = {};
    originalValues.value = {};
    
    // Start new edit
    editingItems.value[index] = true;
    originalValues.value[index] = {
        aantal: item.aantal
    };
};

const cancelEdit = (item, index) => {
    // Restore original values
    item.aantal = originalValues.value[index].aantal;
    
    // Exit edit mode
    editingItems.value[index] = false;
    delete originalValues.value[index];
};

const saveEdit = async (item, index) => {
    try {
        // Set loading state
        savingItems.value[index] = true;
        
        // Validate the data before sending
        const validation = validateOrderDetailData(item);
        if (!validation.isValid) {
            alert(`Validatiefout: ${validation.errors.join(', ')}`);
            return;
        }

        console.log('Saving order detail changes for item:', item);
        
        // Use the item's opdrachtDetailId as detailId
        const detailId = item.opdrachtDetailId;
        if (!detailId) {
            alert('Fout: Geen opdrachtDetailId gevonden');
            return;
        }

        // Call the API to update the order detail
        // Only send aantal (quantity) - eenheid is no longer editable
        await updateOrderDetail(props.id, detailId, item.aantal);
        
        console.log('Order detail successfully updated');
        
        // Exit edit mode only after successful save
        editingItems.value[index] = false;
        delete originalValues.value[index];
        
        // Optional: Show success message
        // You could add a toast notification here if you have one implemented
        
    } catch (error) {
        console.error('Error saving order detail:', error);
        
        // Show user-friendly error message
        let errorMessage = 'Er is een fout opgetreden bij het opslaan van de order detail gegevens.';
        if (error.response && error.response.status === 400) {
            errorMessage = 'Ongeldige gegevens. Controleer de ingevoerde waarden.';
        } else if (error.response && error.response.status === 404) {
            errorMessage = 'Order detail item niet gevonden.';
        } else if (error.message.includes('Network error')) {
            errorMessage = 'Netwerkfout. Controleer uw internetverbinding.';
        }
        
        alert(errorMessage);
        
        // Don't exit edit mode on error, so user can try again
    } finally {
        // Clear loading state
        savingItems.value[index] = false;
    }
};

// Afvalstof edit functions
const startAfvalEdit = (item, index) => {
    if (!isEditable.value) return;

    // Cancel any existing edits first (single-edit mode)
    Object.keys(editingAfvalItems.value).forEach(editIndex => {
        if (editingAfvalItems.value[editIndex] && editIndex !== index.toString()) {
            // Restore original values for the currently editing item
            const editingItem = _afvalstoffenData.value[editIndex];
            if (originalAfvalValues.value[editIndex] && editingItem) {
                editingItem.hoevKg = originalAfvalValues.value[editIndex].hoevKg;
            }
        }
    });
    
    // Clear all edit states
    editingAfvalItems.value = {};
    originalAfvalValues.value = {};
    
    // Start new edit
    editingAfvalItems.value[index] = true;
    originalAfvalValues.value[index] = {
        hoevKg: item.hoevKg
    };
};

const cancelAfvalEdit = (item, index) => {
    // Restore original values
    item.hoevKg = originalAfvalValues.value[index].hoevKg;
    
    // Exit edit mode
    editingAfvalItems.value[index] = false;
    delete originalAfvalValues.value[index];
};

const saveAfvalEdit = async (item, index) => {
    try {
        // Set loading state
        savingAfvalItems.value[index] = true;
        
        // Validate the data before sending
        const validation = validateAfvalstofData(item);
        if (!validation.isValid) {
            alert(`Validatiefout: ${validation.errors.join(', ')}`);
            return;
        }

        console.log('Saving afvalstof changes for item:', item);
        
        // Use the item's aRId as arId for the API call
        const arId = item.aRId;
        if (!arId) {
            alert('Fout: Geen afvalregister ID gevonden');
            return;
        }

        // Call the API to update the afvalstof
        await updateAfvalstof(arId, item.hoevKg);
        
        console.log('Afvalstof successfully updated');
        
        // Exit edit mode only after successful save
        editingAfvalItems.value[index] = false;
        delete originalAfvalValues.value[index];
        
        // Optional: Show success message
        // You could add a toast notification here if you have one implemented
        
    } catch (error) {
        console.error('Error saving afvalstof:', error);
        
        // Show user-friendly error message
        let errorMessage = 'Er is een fout opgetreden bij het opslaan van de afvalstof gegevens.';
        if (error.response && error.response.status === 400) {
            errorMessage = 'Ongeldige gegevens. Controleer de ingevoerde waarden.';
        } else if (error.response && error.response.status === 404) {
            errorMessage = 'Afvalstof item niet gevonden.';
        } else if (error.message.includes('Network error')) {
            errorMessage = 'Netwerkfout. Controleer uw internetverbinding.';
        }
        
        alert(errorMessage);
        
        // Don't exit edit mode on error, so user can try again
    } finally {
        // Clear loading state
        savingAfvalItems.value[index] = false;
    }
};

</script>

  <template>
      <div v-if="showWerkbon" class="absolute top-0 left-0 z-[10000] w-full h-full">
        <div class="bg-mc-primary w-full px-6 h-[5%] flex items-center justify-center">
            <button @click="showWerkbon = false" class="maxi-button">Opdrachtbon sluiten</button>
        </div>

            <div class="absolute top-0 left-0 bg-gray-800/50 w-full h-screen text-center text-xl py-10" v-if="showWerkbonLoader">
                <span class="bg-gray-900 px-8 py-2 text-white rounded">Opdrachtbon wordt geladen...</span>
            </div>
        <embed class="h-[95%]" ref="iframe" width='100%' height='100%' src='' />

    </div>
        <WerkbonModal />
  <AppLayout>    
    <template #left>
        <div class="header-button">{{ _werkbon?.opdrachtId }}</div>
    </template>
    <template #title>{{ translationStore.t('werkbon.titel') }}</template>
    <div class="flex pb-4 justify-between items-end gap-2">
        <div class="flex w-full gap-4 flex-1">
            <TimeComponent :label="translationStore.t('timeclocks.aankomst_werf')" round="down" key-value="aankomstWerf" :opdracht-id="id"/>
            <TimeComponent :label="translationStore.t('timeclocks.vertrek_werf')" round="up" key-value="vertrekWerf" :opdracht-id="id"/>
        </div>
        <div class="flex gap-2">
            <button @click="openWerkbonModal" class="maxi-button">{{ translationStore.t('werkbon.handtekening_button') }}</button>
            <a v-if="false" href="http://localhost:3000/api/app/rma/v2/opdrachtbon" class="maxi-button">{{ translationStore.t('werkbon.opdrachtbon_button') }}</a>
            <button v-if="hasWerkbon" @click="openWerkbon" class="maxi-button">
                {{ translationStore.t('werkbon.opdrachtbon_button') }}
            </button>
            <button v-else class="maxi-button" @click="openWerkbon">
                {{ translationStore.t('werkbon.voorlopige_opdrachtbon_button') }}
            </button>
            <button @click="openBijlagen" class="maxi-button">{{ translationStore.t('werkbon.bijlagen') }} ({{ _werkbon?.aantalAttachments ?? 0 }})</button>
        </div>
    </div>
<!--     {{ werkbonModalStore.form }}
 -->    <div class="flex flex-col gap-3">
      <div class="grid grid-cols-2 gap-3">
        <PrimaryContainer class="flex flex-col gap-6 leading-tight py-6" :uitgevoerd="_werkbon?.statusUitvoeringOpdracht === 'Uitgevoerd'">
          <div>
            <div class="flex justify-between pr-8">
                <div>
                    <h3>{{ translationStore.t('werkbon.klant') }}</h3>
                    <span class="font-medium">{{ _werkbon?.naam }}</span>
                </div>
                <div>
                    <h3>{{ translationStore.t('werklijst.klant_id') }}</h3>
                    <span class="font-medium">{{ _werkbon?.klantId }}</span>
                </div>
            </div>
          </div>
          <div>
            <h3>{{ translationStore.t('werkbon.werfadres') }}</h3>
            <span class="font-medium">
                {{ _werkbon?.opdrachtStraat }} {{ _werkbon?.opdrachtHuisNr }}
                <template v-if="_werkbon?.opdrachtPostcode || _werkbon?.opdrachtGemeente">,</template>
                {{ _werkbon?.opdrachtPostcode }} {{ _werkbon?.opdrachtGemeente }}
            </span>
          </div>
          <div class="grid grid-cols-2">
            <div>
                <h3>{{ translationStore.t('werkbon.contact') }}</h3>
                <span class="font-medium">
                    {{ _werkbon?.opdrachtContactPersoon }}
                </span>
            </div>
            <div>
                <h3>{{ translationStore.t('werkbon.contact_telefoon') }}</h3>
                <span class="font-medium">
                    {{ _werkbon?.opdrachtTelNr }}
                </span>
            </div>
          </div>
        </PrimaryContainer>
        <PrimaryContainer class="flex flex-col gap-6 leading-tight py-6" :uitgevoerd="_werkbon?.statusUitvoeringOpdracht === 'Uitgevoerd'">
          <div>
            <h3>{{ translationStore.t('werkbon.facturatie') }}</h3>
            <span class="font-medium">
                {{ _werkbon?.factNaam }}
            </span>
          </div>
          <div>
            <h3>{{ translationStore.t('werkbon.factuuradres') }}</h3>
            <span class="font-medium">
                {{ _werkbon?.factStraat }} {{ _werkbon?.factHuisNr }}
                <template v-if="_werkbon?.factPostcode || _werkbon?.factGemeente">,</template>
                {{ _werkbon?.factPostcode }} {{ _werkbon?.factGemeente }}
            </span>
          </div>
          <div class="grid grid-cols-2">
            <div>
              <h3>{{ translationStore.t('werkbon.btw') }}</h3>
              <span class="font-medium">
                {{ _werkbon?.bTWnr }}
              </span>
            </div>
            <div>
              <h3>{{ translationStore.t('werkbon.contact_telefoon') }}</h3>
              <span class="font-medium">
                {{ _werkbon?.telNr }}
              </span>
            </div>
          </div>
        </PrimaryContainer>
      </div>
      <div class="grid grid-cols-2 gap-2 leading-tight">
        <PrimaryContainer class="py-6" :uitgevoerd="_werkbon?.statusUitvoeringOpdracht === 'Uitgevoerd'">
          <h3>{{ translationStore.t('werkbon.opdracht') }}</h3>
          <p class="font-medium">
            {{ _werkbon?.opdrachtOmschKort }}
          </p>
        </PrimaryContainer>
        <PrimaryContainer class="py-6" :uitgevoerd="_werkbon?.statusUitvoeringOpdracht === 'Uitgevoerd'">
          <h3>{{ translationStore.t('werkbon.opmerking') }}</h3>
          <p class="font-medium">
            {{ _werkbon?.opmerking }}
          </p>
        </PrimaryContainer>
      </div>
      <div class="text-sm">
        <div class="grid grid-cols-12 px-4 font-medium pb-2">
          <span class="col-span-3">{{ translationStore.t('werkbon.actie') }}</span>
          <span class="col-span-1 text-center">{{ translationStore.t('werkbon.aantal') }}</span>
          <span class="col-span-2 text-center">{{ translationStore.t('werkbon.unit') }}</span>
          <span class="col-span-2 text-center">{{ translationStore.t('werkbon.eenheidsprijs') }}</span>
          <span class="col-span-2 text-center">{{ translationStore.t('werkbon.subtotaal') }}</span>
          <span class="col-span-2 text-right">Acties</span>
        </div>
        <div>
          <div class="flex flex-col">
            <div v-for="(item, index) in _werkbonDetails" :key="item" class="grid grid-cols-12 py-2 px-4 w-full"
                :class="[
                    _werkbon?.statusUitvoeringOpdracht === 'Uitgevoerd' ? 
                        index % 2 == 0 ? 'bg-gray-400' : 'bg-gray-500' :
                        index % 2 == 0 ? 'bg-mc-primary' : 'bg-mc-secondary',
                    index == _werkbonDetails.length - 1 ? 'rounded-b-lg' : '',
                    index == 0 ? 'rounded-t-lg' : ''
                    ]"
            >
                <span class="col-span-3">{{ item?.omschrijving }}</span>
                
                <!-- Aantal (Quantity) -->
                <div class="col-span-1 flex justify-center">
                    <span v-if="!editingItems[index]" class="text-center py-1">{{ item?.aantal }}</span>
                    <input 
                        v-else
                        type="number"
                        v-model="item.aantal"
                        :disabled="!isEditable"
                        class="w-16 px-2 py-1 text-center text-sm rounded border backdrop-blur-sm text-black placeholder-gray-600 focus:bg-black/20 focus:outline-none transition-all"
                        :class="[!isEditable ? 'bg-gray-400/50' : 'border-black/20 hover:bg-black/15']"
                        step="0.01"
                        min="0"
                    />
                </div>
                
                <!-- Eenheid (Unit) - No longer editable -->
                <div class="col-span-2 flex justify-center">
                    <span class="text-center py-1">{{ item?.eenheid }}</span>
                </div>
                
                <span class="col-span-2 text-center">{{ item?.eenheidsprijs.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' }) }}</span>
                <span class="col-span-2 text-center">{{ (parseFloat(item?.aantal) * parseFloat(item?.eenheidsprijs)).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' }) }}</span>
                
                <!-- Action buttons -->
                <div class="col-span-2 flex justify-end items-center gap-1">
                    <button
                        v-if="!editingItems[index] && isEditable"
                        @click="startEdit(item, index)"
                        class="px-3 py-1 text-xs rounded-md bg-black/10 hover:bg-black/20 transition-all duration-200 text-black font-medium whitespace-nowrap"
                    >
                        {{ translationStore.t('werkbon.aanpassen') }}
                    </button>
                    
                    <template v-if="editingItems[index]">
                        <button 
                            @click="saveEdit(item, index)"
                            :disabled="savingItems[index]"
                            class="px-2 py-1 text-xs rounded-md bg-green-100 hover:bg-green-200 transition-all duration-200 text-green-800 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span v-if="savingItems[index]">Opslaan...</span>
                            <span v-else>{{ translationStore.t('werkbon.opslaan') }}</span>
                        </button>
                        <button 
                            @click="cancelEdit(item, index)"
                            :disabled="savingItems[index]"
                            class="px-2 py-1 text-xs rounded-md bg-red-100 hover:bg-red-200 transition-all duration-200 text-red-800 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ translationStore.t('werkbon.annuleren') }}
                        </button>
                    </template>
                </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Afvalstoffen Table -->
      <div class="text-sm mt-6">
        <div class="grid grid-cols-12 px-4 font-medium pb-2">
          <span class="col-span-2">{{ translationStore.t('werkbon.afvalstofcode') }}</span>
          <span class="col-span-6">{{ translationStore.t('werkbon.omschrijving') }}</span>
          <span class="col-span-2 text-center">{{ translationStore.t('werkbon.hoev_kg') }}</span>
          <span class="col-span-2 text-right">Acties</span>
        </div>
        <div>
          <div class="flex flex-col">
            <div v-for="(item, index) in _afvalstoffenData" :key="item.aRId || item.afvalstofcode || index" class="grid grid-cols-12 py-2 px-4 w-full items-center"
                :class="[
                    _werkbon?.statusUitvoeringOpdracht === 'Uitgevoerd' ? 
                        index % 2 == 0 ? 'bg-gray-400' : 'bg-gray-500' :
                        index % 2 == 0 ? 'bg-mc-primary' : 'bg-mc-secondary',
                    index == _afvalstoffenData.length - 1 ? 'rounded-b-lg' : '',
                    index == 0 ? 'rounded-t-lg' : ''
                    ]"
            >
                <!-- Afvalstofcode -->
                <span class="col-span-2">{{ item?.afvalstofcode }}</span>
                
                <!-- Omschrijving -->
                <span class="col-span-6">{{ item?.omschrijving }}</span>
                
                <!-- Hoev.kg -->
                <div class="col-span-2 flex justify-center">
                    <span v-if="!editingAfvalItems[index]" class="text-center py-1">{{ item?.hoevKg }}</span>
                    <input 
                        v-else
                        type="number"
                        v-model="item.hoevKg"
                        :disabled="!isEditable"
                        class="w-16 px-2 py-1 text-center text-sm rounded border backdrop-blur-sm text-black placeholder-gray-600 focus:bg-black/20 focus:outline-none transition-all w-full"
                        :class="[!isEditable ? 'bg-gray-400/50' : 'border-black/20 hover:bg-black/15']"
                        step="0.1"
                        min="0"
                    />
                </div>
                
                <!-- Action buttons -->
                <div class="col-span-2 flex justify-end items-center gap-1">
                    <button
                        v-if="!editingAfvalItems[index] && isEditable"
                        @click="startAfvalEdit(item, index)"
                        class="px-3 py-1 text-xs rounded-md hover:bg-black/20 transition-all duration-200 text-black font-medium whitespace-nowrap"
                    >
                        {{ translationStore.t('werkbon.aanpassen') }}
                    </button>
                    
                    <template v-if="editingAfvalItems[index]">
                        <button 
                            @click="saveAfvalEdit(item, index)"
                            :disabled="savingAfvalItems[index]"
                            class="px-2 py-1 text-xs rounded-md bg-green-100 hover:bg-green-200 transition-all duration-200 text-green-800 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span v-if="savingAfvalItems[index]">Opslaan...</span>
                            <span v-else>{{ translationStore.t('werkbon.opslaan') }}</span>
                        </button>
                        <button 
                            @click="cancelAfvalEdit(item, index)"
                            :disabled="savingAfvalItems[index]"
                            class="px-2 py-1 text-xs rounded-md bg-red-100 hover:bg-red-200 transition-all duration-200 text-red-800 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ translationStore.t('werkbon.annuleren') }}
                        </button>
                    </template>
                </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  </AppLayout>
  <ReturnButton>{{ translationStore.t('werkbon.terug_naar_werklijst') }}</ReturnButton>
</template>
